import type { MessageDescriptor } from "@lingui/core";
import { sha256 } from "js-sha256";

type MacroDescriptor = { message: string; context?: string; comment?: string };

// Mirrors @lingui/message-utils generateMessageId (an ESM-only package that breaks CJS consumers
// such as the api's jest runner). Uses js-sha256 rather than node:crypto so the shim stays
// isomorphic — it reaches client bundles and webpack rejects node built-ins there. The algorithm
// is a stable Lingui contract — the ids must match the catalog keys the SWC macro produces at
// build time.
const UNIT_SEPARATOR = "\u001f";
function generateMessageId(message: string, context = ""): string {
	const hashBytes = sha256.array(message + UNIT_SEPARATOR + context);
	return btoa(String.fromCharCode(...hashBytes))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "")
		.slice(0, 6);
}

// Tagged templates accept no interpolations (values: never[]): the runtime shim cannot reproduce
// the macro's placeholder names, which would silently break catalog lookups. Use the descriptor
// form with explicit ICU placeholders and pass values at the render site instead.
export function msg(descriptor: MacroDescriptor): MessageDescriptor;
export function msg(strings: TemplateStringsArray, ...values: never[]): MessageDescriptor;
export function msg(arg: MacroDescriptor | TemplateStringsArray): MessageDescriptor {
	if (Array.isArray(arg)) {
		const message = arg.join("");
		return { id: generateMessageId(message), message };
	}
	const { message, context, comment } = arg as MacroDescriptor;
	return {
		id: generateMessageId(message, context),
		message,
		...(context ? { context } : {}),
		...(comment ? { comment } : {}),
	};
}
